<template>
  <BaseModal
    :heading="isEdit ? t('package.edit') : t('package.create')"
    :loading="loading"
    :parentRoute="parentRouteName"
    testId="package"
    useForm
    grey
    @close="close"
    @submit="submit"
  >
    <Flex
      direction="column"
      :class="[$style.base, {[$style.smallScreen]: $screen === 's'}]"
    >
      <BaseAlert
        v-if="errorResponse"
        color="error"
        :text="errorResponse"
        v-test="'package-error'"
      />

      <BaseCard :heading="t('package.details_heading')">
        <Flex direction="column">
          <Flex :direction="$screen === 's' ? 'column' : 'row'">
            <div :class="$style.nameInput">
              <BaseInput
                v-model="formData.name"
                :label="t('package.name')"
                :maxLength="255"
                required
                v-test="'package-name'"
              />
            </div>
            <div :class="$style.categoryDropdown">
              <BaseDropdown
                v-model="formData.categoryId"
                :options="[
                  ...serviceCategories.map((category) => ({
                    value: category.id,
                    label: category.name
                  }))
                ]"
                required
                :label="t('global.items.category', 1)"
                v-test="'package-category'"
              />
            </div>
          </Flex>
          <BaseInput
            v-model="formData.description"
            :label="t('package.description')"
            :placeholder="t('package.description_placeholder')"
            type="textarea"
            v-test="'package-description'"
          />
        </Flex>
      </BaseCard>

      <PackageServices v-model="formData.items" />

      <PackagePricing
        v-model:priceMode="formData.priceMode"
        v-model:fixedPrice="formData.fixedPrice"
        v-model:discountPercentage="formData.discountPercentage"
        :items="formData.items"
      />

      <BaseCard>
        <BaseToggle
          v-model="formData.bookable"
          :label="t('package.bookable_heading')"
          :description="t('package.bookable_description')"
          v-test="'package-bookable'"
        />
      </BaseCard>
    </Flex>

    <template
      v-if="isEdit"
      #footerSub
    >
      <BaseText
        color="error"
        @click="deletePackage"
        v-test="'package-delete'"
      >
        {{ t('package.delete') }}
      </BaseText>
    </template>
    <template #footer>
      <BaseButton
        color="inverted"
        @click="close"
        v-test="'package-cancel'"
      >
        {{ t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        :loading="saving"
        submitForm
        v-test="'package-submit'"
      >
        {{ t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useLeaveConfirmation } from '@/helpers/routing';
import { flash, modal } from '@/helpers/ui';
import { useServicesStore } from '@/stores/services';
import { ServicePackagePricingTypeEnum } from '@/types';
import {
  CREATE_SERVICE_PACKAGE,
  DELETE_SERVICE_PACKAGE,
  GET_SERVICE_PACKAGE,
  UPDATE_SERVICE_PACKAGE,
} from './graphql';
import PackagePricing from './PackagePricing.vue';
import PackageServices from './PackageServices.vue';
import type { PackageFormData } from './types';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const props = defineProps<{
  parentRouteName?: string;
}>();

const emit = defineEmits(['close']);

const { serviceCategories } = storeToRefs(useServicesStore());
const { getServicePackages } = useServicesStore();

const activeTab = inject<Ref<'services' | 'packages'>>('services-active-tab');

const packageId = computed(() =>
  route.params.packageId ? String(route.params.packageId) : null,
);
const isEdit = computed(() => !!packageId.value);

const formData = reactive<PackageFormData>({
  name: '',
  categoryId: null,
  description: '',
  bookable: true,
  priceMode: 'service_prices',
  fixedPrice: null,
  discountPercentage: null,
  items: [],
});

const { resetConfirmation } = useLeaveConfirmation(formData);

const errorResponse = ref('');
const loading = ref(false);
if (isEdit.value) {
  loading.value = true;
  const { onResult, onError } = useQuery(GET_SERVICE_PACKAGE, {
    id: Number(packageId.value),
  });
  onError(() => {
    loading.value = false;
  });
  onResult(({ data }) => {
    const servicePackage = data?.servicePackage;

    if (!servicePackage) {
      loading.value = false;
      return;
    }

    formData.id = servicePackage.id;
    formData.name = servicePackage.name;
    formData.categoryId = servicePackage.category?.id ?? null;
    formData.description = servicePackage.description ?? '';
    formData.bookable = servicePackage.bookable;
    formData.discountPercentage = servicePackage.discountPercentage;
    formData.fixedPrice = servicePackage.fixedPrice;
    formData.priceMode =
      servicePackage.pricingType === ServicePackagePricingTypeEnum.Fixed
        ? 'fixed'
        : servicePackage.discountPercentage
          ? 'percentage'
          : 'service_prices';
    formData.items = servicePackage.servicePackageServices.map(
      (servicePackageService: {
        id: number;
        originalPrice: number;
        service: { id: number };
      }) => ({
        _uid: servicePackageService.id,
        id: servicePackageService.id,
        serviceId: servicePackageService.service.id,
        originalPrice: servicePackageService.originalPrice,
      }),
    );

    loading.value = false;
    nextTick(resetConfirmation);
  });
} else {
  nextTick(resetConfirmation);
}

const buildInput = () => {
  const pricingType =
    formData.priceMode === 'fixed'
      ? ServicePackagePricingTypeEnum.Fixed
      : ServicePackagePricingTypeEnum.Percentage;

  return {
    name: formData.name,
    categoryId: formData.categoryId,
    description: formData.description || null,
    bookable: formData.bookable,
    pricingType,
    fixedPrice: formData.priceMode === 'fixed' ? formData.fixedPrice : 0,
    discountPercentage:
      formData.priceMode === 'percentage' ? formData.discountPercentage : 0,
    servicePackageServicesAttributes: formData.items
      .filter((item) => item.serviceId != null)
      .map((item, index) => ({
        id: item.id,
        serviceId: item.serviceId,
        originalPrice: item.originalPrice,
        sortOrder: index,
        destroy: item.destroy,
      })),
  };
};

const {
  mutate: createServicePackage,
  loading: creating,
  onDone: onCreated,
} = useMutation(CREATE_SERVICE_PACKAGE, {
  refetchQueries: ['getServicePackages'],
});
const {
  mutate: updateServicePackage,
  loading: updating,
  onDone: onUpdated,
} = useMutation(UPDATE_SERVICE_PACKAGE, {
  refetchQueries: ['getServicePackages'],
});
const { mutate: deleteServicePackage, onDone: onDeleted } = useMutation(
  DELETE_SERVICE_PACKAGE,
  {
    refetchQueries: ['getServicePackages'],
  },
);

// refetchQueries only refreshes this module's queries, the calendar reads the packages from the store
[onCreated, onUpdated, onDeleted].forEach((onDone) => {
  onDone(() => getServicePackages());
});

const saving = computed(() => creating.value || updating.value);

const submit = () => {
  errorResponse.value = '';

  const mutate = isEdit.value ? updateServicePackage : createServicePackage;
  const input = isEdit.value
    ? { ...buildInput(), id: formData.id }
    : buildInput();

  mutate({ input }).then(({ data }) => {
    const payload = isEdit.value
      ? data.updateServicePackage
      : data.createServicePackage;

    if (payload.errors) {
      errorResponse.value = t('global.error_generic');
      return;
    }

    resetConfirmation();
    flash(
      t(`global.flash.${isEdit.value ? 'package_updated' : 'package_created'}`),
    );
    if (activeTab) {
      activeTab.value = 'packages';
    }
    close();
  });
};

const deletePackage = () => {
  modal('confirmation', {
    type: 'delete',
    item: formData.name,
    subMessage: t('package.delete_warning'),
  }).then(() => {
    deleteServicePackage({ input: { id: formData.id } }).then(() => {
      resetConfirmation();
      flash(t('global.flash.package_deleted'));
      close();
    });
  });
};

const close = () => {
  if (props.parentRouteName) {
    router.push({ name: props.parentRouteName });
  } else {
    emit('close');
  }
};
</script>

<style
  lang="scss"
  module
>
.nameInput {
  flex-grow: 1;
}
.categoryDropdown {
  flex-shrink: 0;
  width: calc(100% * 0.33);

  .base.smallScreen & {
    width: 100%;
  }
}
</style>
